import {RSVP} from '../../rsvp-backend/src/types/interfaces';

interface ApiErrorResponse {
    error: string;
}

interface RSVPStats {
    totalInvited: number;
    totalConfirmed: number;
    totalDeclined: number;
    totalPending: number;
}

// Request options interface
interface RequestOptions extends RequestInit {
    headers?: Record<string, string>;
}

class RSVPApiService {
    private readonly baseURL: string;

    constructor() {
        this.baseURL = process.env.REACT_APP_API_BASE_URL || '';
    }

    private async makeRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        const config: RequestInit = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        };

        const response = await fetch(url, config);

        if (!response.ok) {
            let errorData: ApiErrorResponse | null = null;
            let errorText: string | null = null;
            try {
                errorData = await response.json();
            } catch (e) {
                errorText = await response.text();
            }
            const errorMessage = errorData?.error || errorText || `HTTP ${response.status}: ${response.statusText}`;
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    async getAllRSVPs(): Promise<RSVP[]> {
        return await this.makeRequest<RSVP[]>('/rsvps', {
            method: 'GET'
        });
    }

    async getRSVPByCode(code: string): Promise<RSVP> {
        if (!code) {
            throw new Error('RSVP code is required');
        }

        return await this.makeRequest<RSVP>(`/rsvp/${encodeURIComponent(code)}`, {
            method: 'GET'
        });
    }

    async updateRSVP(rsvp: RSVP): Promise<RSVP> {
        if (!rsvp || !rsvp.code) {
            throw new Error('RSVP object with code is required');
        }

        return await this.makeRequest<RSVP>(`/rsvp/${encodeURIComponent(rsvp.code)}`, {
            method: 'PUT',
            body: JSON.stringify(rsvp)
        });
    }

    async addRSVP(rsvp: RSVP): Promise<RSVP> {
        if (!rsvp) {
            throw new Error('RSVP object is required');
        }

        return await this.makeRequest<RSVP>('/rsvp', {
            method: 'POST',
            body: JSON.stringify(rsvp)
        });
    }

    async getStats(): Promise<RSVPStats> {
        return await this.makeRequest<RSVPStats>('/stats', {
            method: 'GET'
        });
    }
}

function RSVPServiceInit(){
    return new RSVPApiService();
}

const RSVPService = RSVPServiceInit();
export default RSVPService;